import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import Footer from './Footer';
import {
    LuSettings,
    LuPalette,
    LuBell,
    LuShield,
    LuGlobe,
    LuSun,
    LuMoon,
    LuMonitor,
    LuDownload,
    LuTrash2,
    LuCheck
} from 'react-icons/lu';

function Settings() {
    const { t, lang, setLang } = useLanguage();
    const { isDark, toggleTheme } = useTheme();
    const { user, updateSettings, exportUserData } = useAuth();

    const [activeSection, setActiveSection] = useState('general');
    const [savedMessage, setSavedMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // General settings
    const [selectedLanguage, setSelectedLanguage] = useState(lang);
    const [selectedCurrency, setSelectedCurrency] = useState(user?.settings?.currency || 'USDT');
    const [dateFormat, setDateFormat] = useState(() => localStorage.getItem('dateFormat') || 'dmy');

    // Notification settings
    const [emailNotifications, setEmailNotifications] = useState(user?.settings?.notifications?.email ?? true);
    const [pushNotifications, setPushNotifications] = useState(user?.settings?.notifications?.push ?? true);
    const [marketingEmails, setMarketingEmails] = useState(() => localStorage.getItem('marketingEmails') === 'true');

    // Privacy settings
    const [analyticsEnabled, setAnalyticsEnabled] = useState(() => localStorage.getItem('analyticsEnabled') !== 'false');
    const [shareData, setShareData] = useState(() => localStorage.getItem('shareData') === 'true');

    const showSavedMessage = (message) => {
        setSavedMessage(message);
        setTimeout(() => setSavedMessage(''), 3000);
    };

    const handleSave = async () => {
        setError('');
        setSaving(true);

        try {
            await updateSettings({
                language: selectedLanguage,
                theme: isDark ? 'dark' : 'light',
                currency: selectedCurrency,
                notifications: {
                    email: emailNotifications,
                    push: pushNotifications
                }
            });

            if (selectedLanguage !== lang) {
                setLang(selectedLanguage);
            }

            localStorage.setItem('dateFormat', dateFormat);
            localStorage.setItem('marketingEmails', String(marketingEmails));
            localStorage.setItem('analyticsEnabled', String(analyticsEnabled));
            localStorage.setItem('shareData', String(shareData));

            showSavedMessage(t('profile.saved'));
        } catch (err) {
            setError(err.message || (lang === 'tr' ? 'Ayarlar kaydedilemedi' : 'Failed to save settings'));
        } finally {
            setSaving(false);
        }
    };

    const handleExportData = async () => {
        setError('');
        try {
            const data = await exportUserData();
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `getiri-export-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            showSavedMessage(lang === 'tr' ? 'Verileriniz indirildi' : 'Your data has been downloaded');
        } catch (err) {
            setError(err.message || (lang === 'tr' ? 'Veriler indirilemedi' : 'Data export failed'));
        }
    };

    const handleDeleteData = async () => {
        if (window.confirm(lang === 'tr'
            ? 'Tüm hesaplama geçmişiniz, favorileriniz ve hatırlatıcılarınız silinecek. Devam etmek istiyor musunuz?'
            : 'All your calculation history will be deleted. Do you want to continue?'
        )) {
            localStorage.removeItem('calculationHistory');
            localStorage.removeItem('getiri-favorites');
            localStorage.removeItem('getiri-reminders');

            showSavedMessage(lang === 'tr' ? 'Yerel verileriniz silindi' : 'Your local data has been deleted');
        }
    };

    const sections = [
        { id: 'general', label: t('settings.general'), icon: LuSettings },
        { id: 'appearance', label: t('settings.appearance'), icon: LuPalette },
        { id: 'notifications', label: t('settings.notifications'), icon: LuBell },
        { id: 'privacy', label: t('settings.privacy'), icon: LuShield }
    ];

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div className="app-container">
            <Navbar />

            <main className="main-content">
                <div className="settings-page">
                    <div className="settings-header">
                        <h1>{t('settings.title')}</h1>
                    </div>

                    {savedMessage && (
                        <div className="alert alert-success">
                            <LuCheck /> {savedMessage}
                        </div>
                    )}
                    {error && (
                        <div className="alert alert-error">
                            {error}
                        </div>
                    )}

                    <div className="settings-content">
                        {/* Sidebar */}
                        <div className="settings-sidebar">
                            {sections.map((section) => (
                                <button
                                    key={section.id}
                                    className={`settings-nav-item ${activeSection === section.id ? 'active' : ''}`}
                                    onClick={() => setActiveSection(section.id)}
                                >
                                    <section.icon />
                                    {section.label}
                                </button>
                            ))}
                        </div>

                        {/* Main Content */}
                        <div className="settings-main">
                            {/* General Settings */}
                            {activeSection === 'general' && (
                                <div className="settings-section">
                                    <h2>{t('settings.general')}</h2>

                                    <div className="setting-item">
                                        <div className="setting-info">
                                            <LuGlobe />
                                            <div>
                                                <h4>{t('settings.language')}</h4>
                                                <p>{lang === 'tr' ? 'Uygulama dilini secin' : 'Select application language'}</p>
                                            </div>
                                        </div>
                                        <select
                                            className="form-select"
                                            value={selectedLanguage}
                                            onChange={(e) => setSelectedLanguage(e.target.value)}
                                        >
                                            <option value="tr">Türkçe</option>
                                            <option value="en">English</option>
                                        </select>
                                    </div>

                                    <div className="setting-item">
                                        <div className="setting-info">
                                            <LuSettings />
                                            <div>
                                                <h4>{t('settings.currency')}</h4>
                                                <p>{lang === 'tr' ? 'Varsayilan para birimi' : 'Default currency'}</p>
                                            </div>
                                        </div>
                                        <select
                                            className="form-select"
                                            value={selectedCurrency}
                                            onChange={(e) => setSelectedCurrency(e.target.value)}
                                        >
                                            <option value="USDT">USDT</option>
                                            <option value="USD">USD ($)</option>
                                            <option value="TRY">TRY (₺)</option>
                                            <option value="EUR">EUR (€)</option>
                                        </select>
                                    </div>

                                    <div className="setting-item">
                                        <div className="setting-info">
                                            <LuSettings />
                                            <div>
                                                <h4>{t('settings.dateFormat')}</h4>
                                                <p>{lang === 'tr' ? 'Tarih gosterim formati' : 'Date display format'}</p>
                                            </div>
                                        </div>
                                        <select className="form-select" value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
                                            <option value="dmy">DD/MM/YYYY</option>
                                            <option value="mdy">MM/DD/YYYY</option>
                                            <option value="ymd">YYYY-MM-DD</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Appearance Settings */}
                            {activeSection === 'appearance' && (
                                <div className="settings-section">
                                    <h2>{t('settings.appearance')}</h2>

                                    <div className="theme-options">
                                        <div
                                            className={`theme-option ${!isDark ? 'active' : ''}`}
                                            onClick={() => isDark && toggleTheme()}
                                        >
                                            <div className="theme-preview light">
                                                <LuSun />
                                            </div>
                                            <span>{t('settings.lightMode')}</span>
                                        </div>
                                        <div
                                            className={`theme-option ${isDark ? 'active' : ''}`}
                                            onClick={() => !isDark && toggleTheme()}
                                        >
                                            <div className="theme-preview dark">
                                                <LuMoon />
                                            </div>
                                            <span>{t('settings.darkMode')}</span>
                                        </div>
                                        <div className="theme-option">
                                            <div className="theme-preview system">
                                                <LuMonitor />
                                            </div>
                                            <span>{t('settings.systemDefault')}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Notification Settings */}
                            {activeSection === 'notifications' && (
                                <div className="settings-section">
                                    <h2>{t('settings.notifications')}</h2>

                                    <div className="setting-item">
                                        <div className="setting-info">
                                            <LuBell />
                                            <div>
                                                <h4>{t('profile.emailNotifications')}</h4>
                                                <p>{lang === 'tr' ? 'Onemli guncellemeler icin email al' : 'Receive emails for important updates'}</p>
                                            </div>
                                        </div>
                                        <label className="toggle">
                                            <input
                                                type="checkbox"
                                                checked={emailNotifications}
                                                onChange={(e) => setEmailNotifications(e.target.checked)}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>

                                    <div className="setting-item">
                                        <div className="setting-info">
                                            <LuBell />
                                            <div>
                                                <h4>{t('profile.pushNotifications')}</h4>
                                                <p>{lang === 'tr' ? 'Tarayici bildirimleri' : 'Browser notifications'}</p>
                                            </div>
                                        </div>
                                        <label className="toggle">
                                            <input
                                                type="checkbox"
                                                checked={pushNotifications}
                                                onChange={(e) => setPushNotifications(e.target.checked)}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>

                                    <div className="setting-item">
                                        <div className="setting-info">
                                            <LuBell />
                                            <div>
                                                <h4>{lang === 'tr' ? 'Pazarlama Emailleri' : 'Marketing Emails'}</h4>
                                                <p>{lang === 'tr' ? 'Yeni ozellikler ve teklifler hakkinda bilgi al' : 'Get updates about new features and offers'}</p>
                                            </div>
                                        </div>
                                        <label className="toggle">
                                            <input
                                                type="checkbox"
                                                checked={marketingEmails}
                                                onChange={(e) => setMarketingEmails(e.target.checked)}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Privacy Settings */}
                            {activeSection === 'privacy' && (
                                <div className="settings-section">
                                    <h2>{t('settings.privacy')}</h2>

                                    <div className="setting-item">
                                        <div className="setting-info">
                                            <LuShield />
                                            <div>
                                                <h4>{lang === 'tr' ? 'Analitik' : 'Analytics'}</h4>
                                                <p>{lang === 'tr' ? 'Uygulama kullanim verilerini topla' : 'Collect app usage data'}</p>
                                            </div>
                                        </div>
                                        <label className="toggle">
                                            <input
                                                type="checkbox"
                                                checked={analyticsEnabled}
                                                onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>

                                    <div className="setting-item">
                                        <div className="setting-info">
                                            <LuShield />
                                            <div>
                                                <h4>{lang === 'tr' ? 'Veri Paylasimi' : 'Data Sharing'}</h4>
                                                <p>{lang === 'tr' ? 'Anonim kullanim verilerini paylas' : 'Share anonymous usage data'}</p>
                                            </div>
                                        </div>
                                        <label className="toggle">
                                            <input
                                                type="checkbox"
                                                checked={shareData}
                                                onChange={(e) => setShareData(e.target.checked)}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>

                                    <div className="settings-actions">
                                        <button className="btn btn-secondary" onClick={handleExportData}>
                                            <LuDownload /> {t('settings.exportData')}
                                        </button>
                                        <button className="btn btn-danger" onClick={handleDeleteData}>
                                            <LuTrash2 /> {t('settings.deleteData')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="settings-footer">
                                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                                    {saving ? t('loading.loading') : (lang === 'tr' ? 'Değişiklikleri Kaydet' : 'Save Changes')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default Settings;
